/**
 * Bucketliste auf dem Gerät (18.10.2026). Ein Eintrag ist privat (nur ich —
 * die geheime Liste) oder im Haushalt geteilt. „Planen“ legt einen Termin an
 * — privat in `events`, geteilt in `household_events` — und friert dessen Tag
 * am Eintrag ein; „Erlebt“ macht ihn zur Erinnerung im Tagebuch.
 */

import { newId } from '@kraftwerk/shared';
import { db } from './local.js';
import type {
  KraftwerkDatabase,
  LocalBucketItem,
  LocalEvent,
  LocalHouseholdEvent,
} from './local.js';
import { stamp, writeRow, writeRows } from './write.js';

export async function listBucket(database: KraftwerkDatabase = db): Promise<LocalBucketItem[]> {
  return (await database.bucket_items.toArray())
    .filter((row) => !row.deletedAt)
    .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt));
}

export async function addBucketItem(
  title: string,
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalBucketItem> {
  return writeRow(
    'bucket_items',
    {
      id: newId(),
      updatedAt: stamp(),
      deletedAt: null,
      householdId,
      title: title.trim(),
      note: null,
      status: 'idea',
      eventId: null,
      eventDate: null,
      doneDate: null,
    },
    'upsert',
    database,
  );
}

async function change(
  id: string,
  changes: Partial<LocalBucketItem>,
  database: KraftwerkDatabase,
): Promise<LocalBucketItem | null> {
  const row = await database.bucket_items.get(id);
  if (!row || row.deletedAt) return null;
  return writeRow('bucket_items', { ...row, ...changes, updatedAt: stamp() }, 'upsert', database);
}

/**
 * Planen: Termin anlegen und am Eintrag einfrieren — in einem Zug, damit kein
 * Termin ohne Eintrag (oder umgekehrt) zurückbleibt.
 */
export async function planBucketItem(
  id: string,
  date: string,
  time: string | null,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const row = await database.bucket_items.get(id);
  if (!row || row.deletedAt) return;
  const now = stamp();
  const eventId = newId();
  const fields = {
    id: eventId,
    updatedAt: now,
    deletedAt: null,
    title: row.title,
    date,
    time,
    durationMin: time ? 120 : null,
    endDate: null,
    location: null,
    note: null,
    activity: null,
    activityMin: null,
    cancelled: false,
  };
  await writeRows(
    [
      row.householdId
        ? {
            table: 'household_events' as const,
            row: { ...fields, householdId: row.householdId, forUserIds: [] } as LocalHouseholdEvent,
          }
        : { table: 'events' as const, row: fields as LocalEvent },
      {
        table: 'bucket_items' as const,
        row: { ...row, status: 'planned', eventId, eventDate: date, updatedAt: now },
      },
    ],
    database,
  );
}

export function markBucketDone(id: string, date: string, database: KraftwerkDatabase = db) {
  return change(id, { status: 'done', doneDate: date }, database);
}

/** Zurück zur Idee — etwa wenn der Termin geplatzt ist. */
export function reopenBucketItem(id: string, database: KraftwerkDatabase = db) {
  return change(id, { status: 'idea', eventId: null, eventDate: null, doneDate: null }, database);
}

/** Teilen oder wieder privat machen (privat nur, wer ihn angelegt hat — prüft der Server). */
export function shareBucketItem(
  id: string,
  householdId: string | null,
  database: KraftwerkDatabase = db,
) {
  return change(id, { householdId }, database);
}

export async function removeBucketItem(id: string, database: KraftwerkDatabase = db) {
  const row = await database.bucket_items.get(id);
  if (!row || row.deletedAt) return;
  const now = stamp();
  await writeRow('bucket_items', { ...row, deletedAt: now, updatedAt: now }, 'delete', database);
}

/** Geplant und der Tag ist vorbei — „Erlebt?“ fragen. */
export function overdueBucket(items: readonly LocalBucketItem[], today: string) {
  return items.filter(
    (item) => item.status === 'planned' && item.eventDate && item.eventDate < today,
  );
}
